import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Search } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ResetPasswordButton } from "@/components/ResetPasswordButton";
import { api, errMsg, fmtDate, fmtDuration } from "@/lib/api";

const Stat = ({ label, value, id }) => (
  <div className="bg-white border border-zinc-200 p-5" data-testid={`stat-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
    <p className="font-display text-4xl font-extrabold tracking-tight">{value ?? "—"}</p>
  </div>
);

const isOnline = (d) => d && Date.now() - new Date(d).getTime() < 3 * 60 * 1000;

const UserRow = ({ u, onToggle, onChat }) => (
  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150">
    <td className="px-4 py-3 font-semibold whitespace-nowrap">
      <span className={`inline-block w-2 h-2 mr-2 ${isOnline(u.last_seen) ? "bg-[#059669]" : "bg-zinc-300"}`} title={isOnline(u.last_seen) ? "Online" : "Offline"} />
      {u.nome}
    </td>
    <td className="px-4 py-3 font-semibold whitespace-nowrap">{u.cognome}</td>
    <td className="px-4 py-3"><span className="font-mono text-xs px-2 py-1 bg-zinc-950 text-white">{u.classe}</span></td>
    <td className="px-4 py-3 text-zinc-600 min-w-[180px]">{u.istituto}</td>
    <td className="px-4 py-3 font-mono text-xs text-zinc-600 whitespace-nowrap">{fmtDate(u.created_at)}</td>
    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" data-testid={`user-total-time-${u.id}`}>{fmtDuration(u.total_seconds)}</td>
    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" data-testid={`user-last-session-${u.id}`}>{fmtDuration(u.last_session_seconds)}</td>
    <td className="px-4 py-3 font-mono text-xs">{u.works_count}</td>
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <Switch data-testid="btn-toggle-user-status" checked={u.active} onCheckedChange={() => onToggle(u)} className="data-[state=checked]:bg-[#059669]" />
        <span className={`text-xs font-semibold ${u.active ? "text-[#059669]" : "text-[#DC2626]"}`} data-testid={`user-status-${u.id}`}>{u.active ? "Attivo" : "Disabilitato"}</span>
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="flex gap-2">
      <ResetPasswordButton user={u} />
      <button data-testid={`user-chat-btn-${u.id}`} onClick={() => onChat(u)} className="w-9 h-9 grid place-items-center border border-zinc-200 hover:bg-zinc-950 hover:text-white transition-colors duration-150" title="Apri chat">
        <MessageSquare className="w-4 h-4" />
      </button>
      </div>
    </td>
  </tr>
);

export default function AdminUsers() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const toggle = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/toggle`);
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, active: data.active } : x)));
      toast.success(`${u.nome} ${data.active ? "abilitato" : "disabilitato"}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return users.filter((u) => `${u.nome} ${u.cognome} ${u.classe} ${u.istituto} ${u.email}`.toLowerCase().includes(s));
  }, [users, q]);

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-[#0D9488] mb-3">Console di controllo</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-gradient tracking-tight mb-10">Utenti & Sessioni</h1>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat id="users" label="Studenti iscritti" value={stats.users} />
        <Stat id="active" label="Account attivi" value={stats.active} />
        <Stat id="online" label="Online ora" value={stats.online} />
        <Stat id="classes" label="Classi" value={stats.classes} />
        <Stat id="works" label="Lavori salvati" value={stats.works} />
      </div>
      <div className="bg-white border border-zinc-200">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200">
          <Search className="w-4 h-4 text-zinc-400" />
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, classe, istituto…" className="flex-1 h-9 text-sm focus:outline-none" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{filtered.length} risultati</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-zinc-200 text-left">
                {["Nome", "Cognome", "Classe", "Istituto", "Registrato", "Tempo totale", "Ultima sessione", "Lavori", "Stato", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => <UserRow key={u.id} u={u} onToggle={toggle} onChat={(x) => nav(`/admin/chat?user=${x.id}`)} />)}
              {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-400">Nessuno studente registrato</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
